import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "../lib/api";
import { goBooking } from "../lib/promo";
import { LanguageProvider, useLang, pick, setSiteOverrides } from "../lib/i18n";
import { LogoMark } from "../components/Logo";
import Seo from "../components/Seo";
import Navbar from "../components/site/Navbar";
import CaptionedImage from "../components/CaptionedImage";
import Footer from "../components/site/Footer";

function VillaDetailInner() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [site, setSite] = useState(null);
  const [galleryOpenIndex, setGalleryOpenIndex] = useState(null);
  const [seenGallery, setSeenGallery] = useState(() => new Set());

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/site")
      .then((r) => {
        setSiteOverrides(r.data.content || {});
        setSite(r.data);
      })
      .catch(() => setSite({ error: true }));
  }, []);

  if (!site) {
    return (
      <div data-testid="villa-detail-loader" className="min-h-screen flex items-center justify-center bg-[#FBF9F5]">
        <LogoMark className="w-12 h-12 animate-pulse" color="#9A7B66" />
      </div>
    );
  }

  const villa = site.villas?.find((v) => v.id === id) || site.villas?.[0];
  if (!villa) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FBF9F5] gap-4">
        <p className="text-[#6E675F] font-sans">Villa not found.</p>
        <Link to="/" className="text-[#9A7B66] underline underline-offset-4 text-sm font-sans">{t("vd.back")}</Link>
      </div>
    );
  }

  const amenities = String(villa.amenities || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const galleryImages = String(villa.gallery_images || "")
    .split("\n")
    .map((line) => {
      const [url, alt = "", imageTitle = "", imageDescription = "", caption = "", focalPoint = "50% 0%"] = line.split("|").map((s) => s.trim());
      return { url, alt, imageTitle, imageDescription, caption, focalPoint: focalPoint || "50% 0%" };
    })
    .filter((item) => item.url);
  const settings = site.settings || {};
  const villaName = pick(villa, "name", lang);
  const pageTitle = villaName.toLowerCase().includes("nusadira")
    ? villaName
    : `${villaName} | Nusadira Lembongan Villa`;

  const onReserve = () => {
    // Villa-specific deep link (e.g. Exely room-type) wins; opens embedded /book page on our domain
    const target = villa.booking_url || settings.booking_engine_url;
    if (!target) {
      toast.info(t("book.notConfigured"));
      return;
    }
    goBooking(target);
  };

  return (
    <div data-testid="villa-detail-page" className="bg-[#FBF9F5] min-h-screen">
      <Seo
        title={pageTitle}
        description={`${villaName} at Nusadira Lembongan Villa. ${pick(villa, "specs", lang)} Private pool, ocean views and island concierge on Nusa Lembongan, Bali.`}
        image={mediaUrl(villa.image_url)}
        imageAlt={villa.image_alt || villaName}
      />
      <Navbar
        bookingUrl={settings.booking_engine_url}
        logoUrl={settings.logo_url}
        logoLightUrl={settings.logo_light_url}
        navMenu={settings.nav_menu}
        hiddenSections={String(settings.hidden_sections || "").split(",").map((s) => s.trim()).filter(Boolean)}
      />

      <div className="relative h-[62svh] min-h-[420px] overflow-hidden bg-[#1E1A17]">
        <motion.div
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full"
        >
          <CaptionedImage
            testId="villa-detail-hero-image"
            src={mediaUrl(villa.image_url)}
            alt={villa.image_alt || villaName}
            title={villa.image_title || villaName}
            description={villa.image_description || pick(villa, "description", lang)}
            caption={villa.image_caption || ""}
            focalPoint={villa.image_focal_point || "50% 0%"}
            loading="eager"
            className="w-full h-full"
            imageClassName="w-full h-full object-cover"
          />
        </motion.div>
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(30,26,23,0.05) 0%, rgba(30,26,23,0.0) 50%, rgba(30,26,23,0.45) 100%)" }}
        />
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <Link
          data-testid="villa-back-link"
          to="/"
          className="inline-flex items-center gap-2 text-[#9A7B66] hover:text-[#826552] text-[12px] font-sans font-semibold uppercase tracking-[0.16em] mb-10 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("vd.back")}
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="text-xs uppercase tracking-[0.25em] font-sans font-medium text-[#9A7B66] mb-4">{t("villas.eyebrow")}</p>
          <h1 data-testid="villa-detail-title" className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-4xl sm:text-5xl">
            {pick(villa, "name", lang)}
          </h1>
          <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-[#9A7B66] font-sans font-semibold">{pick(villa, "specs", lang)}</p>
          <div className="mt-6 max-w-3xl space-y-5">
            {pick(villa, "description", lang).split(/\n\s*\n/).filter(Boolean).map((para, i) => (
              <p key={i} data-testid={`villa-detail-paragraph-${i}`} className="text-lg text-[#6E675F] font-sans font-light leading-relaxed">{para}</p>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-6 border-t border-b border-[#E5DED4] py-6">
            <div>
              <span className="text-xs uppercase tracking-[0.18em] text-[#A0988E] font-sans">{t("vd.from")}</span>
              <div className="font-serif text-3xl text-[#2C2825]">
                {villa.price}
                <span className="text-sm text-[#A0988E] font-sans"> {t("vd.perNight")}</span>
              </div>
            </div>
            <button
              data-testid="villa-detail-reserve-button"
              onClick={onReserve}
              className="group ml-auto inline-flex items-center gap-2 px-8 py-3.5 rounded-sm bg-[#9A7B66] hover:bg-[#826552] text-[#FBF9F5] text-[12px] font-sans font-semibold uppercase tracking-[0.16em] transition-all duration-300"
            >
              {t("vd.book")}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </motion.div>

        {amenities.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="mt-16"
          >
            <h2 className="font-serif font-normal text-2xl sm:text-3xl text-[#2C2825] mb-8">{t("vd.amenities")}</h2>
            <div data-testid="villa-amenities" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
              {amenities.map((a, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-[#9A7B66] mt-1 shrink-0" />
                  <span className="text-sm font-sans text-[#6E675F] leading-relaxed">{a}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {galleryImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="mt-16"
          >
            <h2 className="font-serif font-normal text-2xl sm:text-3xl text-[#2C2825] mb-8">{t("vd.gallery")}</h2>
            <div data-testid="villa-gallery" className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {galleryImages.map((img, i) => (
                <motion.button
                  key={i}
                  type="button"
                  data-testid={`villa-gallery-image-${i}`}
                  onClick={() => {
                    setGalleryOpenIndex(i);
                    setSeenGallery((current) => new Set(current).add(i));
                  }}
                  initial={{ opacity: 0, scale: 0.96 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                  className="relative overflow-hidden rounded-md group text-left"
                >
                  <img
                    src={mediaUrl(img.url)}
                    alt={img.alt || `${villaName} ${i + 1}`}
                    title={img.imageTitle || undefined}
                    aria-description={img.imageDescription || undefined}
                    loading="lazy"
                    style={{ objectPosition: img.focalPoint }}
                    className={`w-full h-40 md:h-48 object-cover transition-all duration-[1.2s] ease-out group-hover:scale-105 ${
                      seenGallery.has(i) ? "saturate-100 brightness-100" : "saturate-[0.72] brightness-[0.88]"
                    }`}
                  />
                  {seenGallery.has(i) && (
                    <span data-testid={`villa-gallery-viewed-${i}`} className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-[#1E1A17]/70 px-2.5 py-1 text-[9px] uppercase tracking-[0.14em] text-white backdrop-blur-sm">
                      <Check className="w-3 h-3 text-[#C9A66B]" /> Viewed
                    </span>
                  )}
                  {img.imageDescription && <span className="sr-only">{img.imageDescription}</span>}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      <Footer settings={settings} />

      <AnimatePresence>
        {galleryOpenIndex !== null && galleryImages[galleryOpenIndex] && (
          <motion.div
            data-testid="villa-gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setGalleryOpenIndex(null)}
            className="fixed inset-0 z-[100] bg-[#1E1A17]/92 flex items-center justify-center p-5"
          >
            <button
              data-testid="villa-gallery-lightbox-close"
              className="absolute top-5 right-5 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              aria-label="Close gallery image"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="relative max-w-5xl max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={mediaUrl(galleryImages[galleryOpenIndex].url)}
                alt={galleryImages[galleryOpenIndex].alt || `${villaName} gallery image`}
                title={galleryImages[galleryOpenIndex].imageTitle || undefined}
                aria-description={galleryImages[galleryOpenIndex].imageDescription || undefined}
                className="max-w-full max-h-[85vh] object-contain rounded-md"
              />
              {galleryImages[galleryOpenIndex].caption && (
                <figcaption data-testid="villa-gallery-caption" className="absolute bottom-4 right-4 max-w-[75%] rounded-sm bg-[#1E1A17]/75 backdrop-blur-sm px-4 py-2 text-xs sm:text-sm text-[#F4EFEA] font-sans text-right">
                  {galleryImages[galleryOpenIndex].caption}
                </figcaption>
              )}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function VillaDetail() {
  return (
    <LanguageProvider>
      <VillaDetailInner />
    </LanguageProvider>
  );
}
